"use client";

import { JSX, useState } from "react";
import { registerFormTranslations } from "@/lib/pagesTranslations";
import {
  FaUserAlt,
  FaEnvelope,
  FaComments,
  FaPhone,
  FaInstagram,
  FaFacebook,
} from "react-icons/fa";

interface RegisterFormProps {
  locale: "pt" | "en";
}

export type RegisterFormTranslations = {
  [key in
  | "nameLabel"
  | "namePlaceholder"
  | "emailLabel"
  | "emailPlaceholder"
  | "phoneLabel"
  | "phonePlaceholder"
  | "instagramLabel"
  | "instagramPlaceholder"
  | "facebookLabel"
  | "facebookPlaceholder"
  | "serviceTitleLabel"
  | "serviceTitlePlaceholder"
  | "serviceDescriptionLabel"
  | "serviceDescriptionPlaceholder"
  | "categoryLabel"
  | "categoryPlaceholder"
  | "priceRangeLabel"
  | "priceRangePlaceholder"
  | "cityLabel"
  | "cityPlaceholder"
  | "serviceAreaLabel"
  | "serviceAreaPlaceholder"
  | "availabilityLabel"
  | "availabilityPlaceholder"
  | "languagesLabel"
  | "languagesPlaceholder"
  | "messageLabel"
  | "messagePlaceholder"
  | "profilePicNote"
  | "submitButton"
  | "successMessage"
  | "typeLabel"
  | "typePlaceholder"
  | "membershipLabel"
  | "membershipPlaceholder"
  | "consentLabel"
  | "consentHelp"
  | "errorMessage"]: string;

} & {
  validation: {
    nameRequired: string;
    emailRequired: string;
    emailInvalid: string;
    messageRequired: string;
    consentRequired: string;
  };
};

export default function RegisterForm({ locale }: RegisterFormProps) {
  const t = registerFormTranslations[locale] as RegisterFormTranslations;

  const [form, setForm] = useState({
    name: "",
    email: "",
    type: "Service",
    membership: "Member",
    message: "",
    phone: "",
    instagram: "",
    facebook: "",
    serviceTitle: "",
    serviceDescription: "",
    category: "",
    priceRange: "",
    city: "",
    serviceArea: "",
    availability: "",
    languages: "",
  });

  type FormFields = keyof typeof form;
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [status, setStatus] = useState("idle");
  const [consent, setConsent] = useState(false);

  const { validation, ...translations } = t;

  const validate = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = "nameRequired";
    if (!form.email.trim()) newErrors.email = "emailRequired";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = "emailInvalid";
    if (!form.message.trim()) newErrors.message = "messageRequired";
    if (!consent) newErrors.consent = "consentRequired";
    return newErrors;
  };

  const handleBlur = (field: FormFields) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors(validate());
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    setTouched({ name: true, email: true, message: true, consent: true });
    if (Object.keys(validationErrors).length > 0) return;

    const res = await fetch("/api/feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...form,
        consent,
        consentTimestamp: new Date().toISOString(),
        formType: "register",
      }),
    });

    if (res.ok) {
      setForm({
        name: "",
        email: "",
        type: "Service",
        membership: "Member",
        message: "",
        phone: "",
        instagram: "",
        facebook: "",
        serviceTitle: "",
        serviceDescription: "",
        category: "",
        priceRange: "",
        city: "",
        serviceArea: "",
        availability: "",
        languages: "",
      });
      setTouched({});
      setErrors({});
      setConsent(false);
      setStatus("sent");
    } else {
      setStatus("error");
    }
  };

  const renderInput = (
    id: FormFields,
    icon: JSX.Element,
    isTextarea = false
  ) => {

    const label = translations[`${id}Label`];
    const placeholder = translations[`${id}Placeholder`];

    return (
      <div className="border-b border-[#F1F5F9] pb-1">
        <label
          htmlFor={id}
          className="text-xs sm:text-sm md:text-base font-bold uppercase tracking-widest flex items-center gap-2"
        >
          {icon} {label}
        </label>
        {isTextarea ? (
          <textarea
            id={id}
            name={id}
            value={form[id]}
            onChange={handleChange}
            onBlur={() => handleBlur(id)}
            placeholder={placeholder}
            className="bg-transparent w-full mt-1 outline-none placeholder:text-[#F1F5F9]/50 resize-none"
            rows={3}
          />
        ) : (
          <input
            id={id}
            name={id}
            value={form[id]}
            onChange={handleChange}
            onBlur={() => handleBlur(id)}
            placeholder={placeholder}
            className="bg-transparent w-full mt-1 outline-none placeholder:text-[#F1F5F9]/50"
          />
        )}
        {touched[id] && errors[id] && (
          <p className="text-red-400 text-xs sm:text-sm md:text-base mt-1">
            {t.validation[errors[id] as keyof typeof t.validation]}
          </p>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="bg-[#1B475D] my-6 text-[#F1F5F9] mx-auto p-8 rounded space-y-8">
      {renderInput("name", <FaUserAlt size={14} />)}
      {renderInput("email", <FaEnvelope size={14} />)}
      {renderInput("phone", <FaPhone size={14} />)}
      {renderInput("instagram", <FaInstagram size={14} />)}
      {renderInput("facebook", <FaFacebook size={14} />)}
      {renderInput("serviceTitle", <></>)}
      {renderInput("serviceDescription", <></>, true)}
      {renderInput("category", <></>)}
      {renderInput("priceRange", <></>)}
      {renderInput("city", <></>)}
      {renderInput("serviceArea", <></>)}
      {renderInput("availability", <></>)}
      {renderInput("languages", <></>)}
      {renderInput("message", <FaComments size={14} />, true)}

      <p className="text-xs sm:text-sm md:text-base text-[#F1F5F9] mt-4 mb-2">
        {t.profilePicNote}{" "}
        <a
          href="mailto:hello.genauhub@gmail.com"
          className="underline hover:text-[#E2E8F0] transition"
        >
          hello.genauhub@gmail.com
        </a>
      </p>

      <button
        type="submit"
        className="w-full border-2 border-[#F1F5F9] text-[#F1F5F9] py-2 text-xs sm:text-sm md:text-base tracking-widest font-bold uppercase hover:bg-[#F1F5F9] hover:text-[#1B475D] transition"
      >
        {t.submitButton}
      </button>
      <label className="flex items-start gap-2 text-xs sm:text-sm md:text-base">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1"
        />
        <span>{t.consentLabel}</span>
      </label>
      <p className="text-xs sm:text-sm md:text-base text-[#F1F5F9]/90 -mt-6">{t.consentHelp}</p>
      {touched.consent && errors.consent === "consentRequired" && (
        <p className="text-red-400 text-xs sm:text-sm md:text-base mt-1">
          {t.validation.consentRequired}
        </p>
      )}

      {status === "sent" && (
        <p className="text-green-400 text-xs sm:text-sm md:text-base mt-2">
          {t.successMessage}
        </p>
      )}
      {status === "error" && (
        <p className="text-red-400 text-xs sm:text-sm md:text-base mt-2">
          {t.errorMessage}
        </p>
      )}
    </form>
  );
}
